import { Calculation } from "../models/Calculation";
import { CalculationField } from "../models/CalculationField";
import { CalculationSchema } from "../models/CalculationSchema";
import { CalculationSchemaUiFieldGroup } from "../models/CalculationSchemaUi";
import { CalculationVisibilityFieldTypeValues } from "../models/CalculationFieldTypeArgs";
import { CalculationsService } from "./CalculationsService";
import { CalculationsTypeGuardService } from "./CalculationsTypeGuardService";

export class CalculationsUiService {

    public static getCalculationSchemaUiFieldGroups = (calculationSchema: CalculationSchema): Array<CalculationSchemaUiFieldGroup> | null => {

        if (!CalculationsTypeGuardService.hasDefinedCalculationSchemaUi(calculationSchema)) return null;
        return calculationSchema.ui?.field_groups as Array<CalculationSchemaUiFieldGroup>;

    }

    public static getValueByPath = (configuration: any, path?: string | string[]): any => {
        if (configuration == null) return undefined;
        if (path === undefined || path === null) return undefined;

        const parts: string[] = Array.isArray(path)
            ? path
            : (String(path).match(/[^.[\]]+/g) || []);

        if (parts.length === 0) return configuration;

        let cur: any = configuration;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (cur == null) return undefined;

            if (part === '*') {
                if (!Array.isArray(cur)) return undefined;
                const remainingParts = parts.slice(i + 1);
                return cur.map((item: any) => CalculationsUiService.getValueByPath(item, remainingParts));
            }

            if (/^\d+$/.test(part)) {
                cur = cur[Number(part)];
            } else {
                cur = cur[part];
            }
        }
        return cur;
    }

    public static isFieldVisibleFromRoot = (field: CalculationField, rootConfiguration?: Calculation | null, _currentPath?: string[]) => {

        if (field.visible === false) return false;
        if (rootConfiguration != null) {
            const flattenedRootConfiguration = CalculationsService.getFlattenedCalculation(rootConfiguration, null);

            const visibleConfigs = field?.field_type_args?.visible_configs;
            if (Array.isArray(visibleConfigs)) {

                let visible = false;
                visibleConfigs.forEach((config: { visible_prop_path: string, visible_prop_value: any, visible_value?: CalculationVisibilityFieldTypeValues, visible_parent?: string }) => {
                    const propPath = config?.visible_prop_path;
                    const expected = config?.visible_prop_value;
                    const expectedValueType = config?.visible_value;
                    const visibleParent = config?.visible_parent;

                    let targetPathParts: string[] = [];

                    if (visibleParent && Array.isArray(_currentPath)) {
                        let foundIndex = -1;
                        for (let i = _currentPath.length - 1; i >= 0; i--) {
                            if (_currentPath[i] === visibleParent && i + 1 < _currentPath.length && /^\d+$/.test(String(_currentPath[i + 1]))) {
                                foundIndex = i;
                                break;
                            }
                        }
                        if (foundIndex !== -1) {
                            const base = _currentPath.slice(0, foundIndex + 2);
                            const propParts = propPath.includes('.') ? propPath.split('.') : [propPath];
                            targetPathParts = [...base, ...propParts];
                        }
                    }

                    if (targetPathParts.length === 0) {
                        targetPathParts = propPath.includes('.')
                            ? propPath.split('.')
                            : (_currentPath ? [..._currentPath.slice(0, -1), propPath] : [propPath]);
                    }

                    const val = CalculationsUiService.getValueByPath(flattenedRootConfiguration, targetPathParts);
                    const flatVal = Array.isArray(val) ? val.flat(Infinity) : val;
                    const matches = Array.isArray(flatVal)
                        ? flatVal.some((v: any) => CalculationsUiService.valueMatchesCalculationVisibilityFieldTypeValue(v, expectedValueType, expected) || v === expected)
                        : CalculationsUiService.valueMatchesCalculationVisibilityFieldTypeValue(flatVal, expectedValueType, expected) || flatVal === expected;
                    visible = visible || matches;
                })
                return visible;
            }
        }
        return true;
    }

    private static valueMatchesCalculationVisibilityFieldTypeValue(value: any, visibilityValue?: CalculationVisibilityFieldTypeValues, expected?: any): boolean {

        switch (visibilityValue) {
            case CalculationVisibilityFieldTypeValues.Defined:
                return value !== undefined;
            case CalculationVisibilityFieldTypeValues.Falsey:
                return !value;
            case CalculationVisibilityFieldTypeValues.Truthy:
                return !!value;
            case CalculationVisibilityFieldTypeValues.Contains:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return expected.includes(value);
                }
                console.warn(`CalculationsUiService: visible_value 'contains' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            case CalculationVisibilityFieldTypeValues.DoesNotContain:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return !expected.includes(value);
                }
                console.warn(`CalculationsUiService: visible_value 'does_not_contain' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            default:
                return false;
        }

    }

}
